import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  PaginationItem,
  CircularProgress,
} from "@mui/material";
import { SortIcon, SortDownIcon } from "../Icons";
import { DataTableStyled } from "./DataTable.styled";

const SortIconComponent = ({
  direction,
  active,
}: {
  direction?: "asc" | "desc";
  active: boolean;
}) => {
  if (!active) {
    return <SortIcon width="20px" height="20px" />;
  }

  if (direction === "asc") {
    return (
      <Box
        sx={{
          transform: "rotate(180deg)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <SortDownIcon width="20px" height="20px" />
      </Box>
    );
  }

  return <SortDownIcon width="20px" height="20px" />;
};

export interface ColumnConfig<T = Record<string, unknown>> {
  key: string;
  title: string;
  sortable?: boolean;
  render?: (value: unknown, row: T) => React.ReactNode;
  width?: string | number;
  align?: "left" | "center" | "right";
}

export interface DataTableProps<
  T extends Record<string, unknown> = Record<string, unknown>,
> {
  columns: ColumnConfig<T>[];
  data: T[];
  loading?: boolean;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  onSort?: (field: string, direction: "asc" | "desc") => void;
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  totalCount?: number;
  searchQuery?: string;
  onSearch?: (query: string) => void;
  emptyMessage?: string;
  emptyContent?: React.ReactNode;
  className?: string;
}

const DataTable = <
  T extends Record<string, unknown> = Record<string, unknown>,
>({
  columns,
  data,
  loading = false,
  sortField,
  sortDirection = "asc",
  onSort,
  pageSize = 25,
  currentPage = 1,
  onPageChange,
  totalCount,
  searchQuery = "",
  // onSearch,
  emptyMessage,
  emptyContent,
  className,
}: DataTableProps<T>) => {
  const { t } = useTranslation();
  const defaultEmptyMessage = emptyMessage || t("dataTable.noData");
  const [internalSortField, setInternalSortField] = useState<string>("");
  const [internalSortDirection, setInternalSortDirection] = useState<
    "asc" | "desc"
  >("asc");
  const [internalPage, setInternalPage] = useState(1);

  const isPaginationControlled = onPageChange !== undefined;
  const isSortControlled = onSort !== undefined;

  const activeSortField = isSortControlled ? sortField : internalSortField;
  const activeSortDirection = isSortControlled
    ? sortDirection
    : internalSortDirection;
  const activePage = isPaginationControlled ? currentPage : internalPage;

  const filteredAndSortedData = useMemo(() => {
    let filtered = data;

    // Only apply client-side filtering and sorting if not controlled (server-side)
    if (!isSortControlled) {
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        filtered = data.filter((row) =>
          Object.values(row).some((value) =>
            String(value).toLowerCase().includes(query),
          ),
        );
      }

      if (activeSortField) {
        const column = columns.find((col) => col.key === activeSortField);
        if (column) {
          filtered = [...filtered].sort((a, b) => {
            const aValue = a[activeSortField];
            const bValue = b[activeSortField];

            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            let comparison = 0;
            if (typeof aValue === "string" && typeof bValue === "string") {
              const isISODate =
                /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(aValue) &&
                /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(bValue);

              if (isISODate) {
                comparison =
                  new Date(aValue).getTime() - new Date(bValue).getTime();
              } else {
                comparison = aValue
                  .toLowerCase()
                  .localeCompare(bValue.toLowerCase());
              }
            } else if (aValue instanceof Date && bValue instanceof Date) {
              comparison = aValue.getTime() - bValue.getTime();
            } else if (typeof aValue === "number" && typeof bValue === "number") {
              comparison = aValue - bValue;
            } else {
              comparison = String(aValue).localeCompare(String(bValue));
            }

            return activeSortDirection === "asc" ? comparison : -comparison;
          });
        }
      }
    }

    return filtered;
  }, [data, searchQuery, activeSortField, activeSortDirection, columns, isSortControlled]);

  const totalPages = totalCount
    ? Math.ceil(totalCount / pageSize)
    : Math.ceil(filteredAndSortedData.length / pageSize);

  const startIndex = (activePage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = totalCount
    ? filteredAndSortedData
    : filteredAndSortedData.slice(startIndex, endIndex);

  const handleSort = (field: string) => {
    const newDirection =
      activeSortField === field && activeSortDirection === "asc"
        ? "desc"
        : "asc";

    if (isSortControlled) {
      onSort?.(field, newDirection);
    } else {
      setInternalSortField(field);
      setInternalSortDirection(newDirection);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    if (isPaginationControlled) {
      onPageChange?.(page);
    } else {
      setInternalPage(page);
    }
  };

  return (
    <DataTableStyled className={className}>
      <TableContainer component={Paper} className="table-container">
        <Box sx={{ position: "relative", flex: 1, overflow: "auto" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column, colIndex) => (
                  <TableCell
                    key={column.key}
                    className="table-header"
                    align={column.align}
                    sx={{
                      width: column.width,
                      ...(colIndex === 0 && { paddingLeft: "8px" }),
                    }}
                  >
                    {column.sortable ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          "&:hover": {
                            opacity: 0.7,
                          },
                        }}
                        onClick={() => handleSort(column.key)}
                      >
                        <Typography
                          sx={{
                            marginRight: "8px",
                            fontWeight: 600,
                            color: "inherit",
                          }}
                        >
                          {column.title}
                        </Typography>
                        <SortIconComponent
                          direction={
                            activeSortField === column.key
                              ? activeSortDirection
                              : undefined
                          }
                          active={activeSortField === column.key}
                        />
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "inherit",
                        }}
                      >
                        {column.title}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {loading ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ border: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "200px",
                      }}
                    >
                      <CircularProgress size={40} />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {currentData.length > 0 ? (
                  currentData.map((row, index) => (
                    <TableRow key={index} className="table-row">
                      {columns.map((column, colIndex) => (
                        <TableCell
                          key={column.key}
                          className="table-cell"
                          align={column.align}
                          sx={
                            colIndex === 0 ? { paddingLeft: "8px" } : undefined
                          }
                        >
                          {column.render
                            ? column.render(row[column.key], row)
                            : String(row[column.key] ?? "")}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      className="no-data-cell"
                    >
                      {emptyContent || (
                        <Typography variant="body1" className="no-data-text">
                          {defaultEmptyMessage}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            )}
          </Table>
        </Box>
      </TableContainer>

      {currentData.length > 0 && (
        <Box className="pagination-section">
          <Typography variant="body2" className="pagination-info">
            {t("dataTable.pagination.showing")} {startIndex + 1}-
            {Math.min(endIndex, totalCount || filteredAndSortedData.length)}{" "}
            {t("dataTable.pagination.of")}{" "}
            {totalCount || filteredAndSortedData.length}{" "}
            {t("dataTable.pagination.results")}
          </Typography>
          <Pagination
            count={Math.max(totalPages, 1)}
            page={activePage}
            onChange={handlePageChange}
            color="primary"
            className="pagination"
            hidePrevButton={false}
            hideNextButton={false}
            showFirstButton={false}
            showLastButton={false}
            siblingCount={0}
            boundaryCount={0}
            renderItem={(item) => {
              if (item.type === "previous" || item.type === "next") {
                return <PaginationItem {...item} />;
              }
              return null;
            }}
          />
        </Box>
      )}
    </DataTableStyled>
  );
};

export default DataTable;
